/**
 * ExtractionProposal Component
 * TASK-373: AI-Assisted KYC Filling Workspace
 *
 * Displays proposed field values from AI with confidence badges.
 * Allows applying individual or all proposals, or skipping them.
 */

import React, { useState } from 'react';
import {
  Check,
  CheckCheck,
  X,
  FileText,
  Sparkles,
} from 'lucide-react';
import { ConfidenceBadge } from '../ConfidenceBadge';

export interface ProposalItem {
  field_path: string;
  value: unknown;
  confidence: number;
  source?: string;
  label?: string; // TASK-479: explicit display label (e.g. position security name)
}

interface ExtractionProposalProps {
  proposals: ProposalItem[];
  onApply: (field_path: string, value: unknown) => void;
  onApplyAll: () => void;
  onSkip: (field_path: string) => void;
}

/**
 * Derive a human-readable label from a dot-notation field path.
 * Handles both camelCase ("personalInfo.firstName" → "First Name")
 * and snake_case ("personal.first_name" → "First Name") paths.
 */
const fieldPathToLabel = (fieldPath: string): string => {
  const segments = fieldPath.split('.');
  // Strip any [index] suffix (KG staging / DNA paths, e.g. "exclusion[0]").
  const lastSegment = segments[segments.length - 1].replace(/\[[^\]]*\]$/, '');

  // Handle snake_case (from backend KG paths)
  if (lastSegment.includes('_')) {
    return lastSegment
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  // Handle camelCase (from frontend form paths)
  return lastSegment
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
};

// Prefer an explicit label (TASK-479 position proposals) over the derived field-path label.
const labelFor = (p: ProposalItem): string => p.label || fieldPathToLabel(p.field_path);

const ExtractionProposal: React.FC<ExtractionProposalProps> = ({
  proposals,
  onApply,
  onApplyAll,
  onSkip,
}) => {
  const [appliedIndices, setAppliedIndices] = useState<Set<number>>(new Set());
  const [skippedIndices, setSkippedIndices] = useState<Set<number>>(new Set());

  const handleApply = (index: number, fieldPath: string, value: unknown) => {
    onApply(fieldPath, value);
    setAppliedIndices((prev) => new Set([...prev, index]));
  };

  const handleSkip = (index: number, fieldPath: string) => {
    onSkip(fieldPath);
    setSkippedIndices((prev) => new Set([...prev, index]));
  };

  const handleApplyAll = () => {
    onApplyAll();
    setAppliedIndices(new Set(proposals.map((_, i) => i)));
  };

  const pendingProposals = proposals.filter(
    (_, i) => !appliedIndices.has(i) && !skippedIndices.has(i)
  );

  if (proposals.length === 0) {
    return null;
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-semibold text-blue-900">
            AI Extraction Proposals
          </span>
          <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
            {pendingProposals.length} pending
          </span>
        </div>
        {pendingProposals.length > 1 && (
          <button
            onClick={handleApplyAll}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
          >
            <CheckCheck className="w-3.5 h-3.5" />
            Apply All
          </button>
        )}
      </div>

      {/* Proposals list */}
      <div className="space-y-2">
        {proposals.map((proposal, index) => {
          const isApplied = appliedIndices.has(index);
          const isSkipped = skippedIndices.has(index);

          if (isApplied) {
            return (
              <div
                key={index}
                className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-md text-sm text-green-700"
              >
                <Check className="w-4 h-4 flex-shrink-0" />
                <span className="font-medium">
                  {labelFor(proposal)}
                </span>
                <span className="text-green-600">Applied</span>
              </div>
            );
          }

          if (isSkipped) {
            return (
              <div
                key={index}
                className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-400 line-through"
              >
                <X className="w-4 h-4 flex-shrink-0" />
                <span>{labelFor(proposal)}</span>
                <span>Skipped</span>
              </div>
            );
          }

          return (
            <div
              key={index}
              className="flex items-center justify-between bg-white border border-gray-200 rounded-md px-3 py-2.5"
            >
              {/* Field info */}
              <div className="flex-1 min-w-0 mr-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {labelFor(proposal)}
                  </span>
                  {/* Confidence badge */}
                  <ConfidenceBadge
                    confidence={proposal.confidence}
                    fieldName={labelFor(proposal)}
                    size="sm"
                  />
                </div>
                <div className="text-sm text-gray-700 truncate">
                  {formatProposalValue(proposal.value)}
                </div>
                {proposal.source && (
                  <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-500">
                    <FileText className="w-3 h-3" />
                    {proposal.source}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => handleApply(index, proposal.field_path, proposal.value)}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 transition-colors"
                  title="Apply this value"
                >
                  <Check className="w-3.5 h-3.5" />
                  Apply
                </button>
                <button
                  onClick={() => handleSkip(index, proposal.field_path)}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors"
                  title="Skip this proposal"
                >
                  <X className="w-3.5 h-3.5" />
                  Skip
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Format a proposal value for display. JSON never renders (TASK-604): DNA
 * proposal values often arrive as JSON *strings* ('{"tag":null,"text":…}') —
 * parse them and show the human text, whatever the encoding.
 */
export function formatProposalValue(value: unknown): string {
  if (value === null || value === undefined) return '(empty)';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') {
    const s = value.trim();
    if (s.startsWith('{') || s.startsWith('[')) {
      try {
        return formatProposalValue(JSON.parse(s));
      } catch {
        // not JSON after all — fall through to the raw string
      }
    }
    return value;
  }
  if (Array.isArray(value)) return value.map(formatProposalValue).filter(Boolean).join(', ');
  if (typeof value === 'object') {
    // DNA items are {text, tag, category, sources}; show the text (+ tag).
    const obj = value as Record<string, unknown>;
    if (typeof obj.text === 'string') {
      return obj.tag ? `${obj.text} · ${String(obj.tag)}` : obj.text;
    }
    // Last resort: the object's primitive values — never raw JSON.
    const primitives = Object.values(obj).filter(
      (v): v is string | number => typeof v === 'string' || typeof v === 'number',
    );
    return primitives.length > 0 ? primitives.join(' · ') : '(structured value)';
  }
  return String(value);
}

export default ExtractionProposal;
